import { useReverification, useUser } from "@clerk/clerk-react";
import { Button, Typography } from "@mui/material";
import { useState } from "react";
import { ProfileSectionTitle } from "./ProfileSectionTitle";

export function ConnectedAccounts() {
  const { user } = useUser();
  const [accountLoading, setAccountLoading] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const disconnectAccount = async (provider: string) => {
    if (!user) return;
    const providerStr = provider === "oauth_google" ? "google" : "microsoft";
    const account = user.externalAccounts.find((a) => a.provider === providerStr);
    if (account) {
      await account.destroy();
      await user.reload();
    }
  };

  const verifiedDisconnect = useReverification(disconnectAccount);

  const handleDisconnect = async (provider: string) => {
    setAccountLoading(provider);
    setDisconnectError(null);
    try {
      await verifiedDisconnect(provider);
    } catch (error: any) {
      const isCancelled =
        error?.clerk_error === "reverification_cancelled" ||
        error?.code === "reverification_cancelled";
      if (!isCancelled) {
        console.error(error);
        setDisconnectError(
          error.errors?.[0]?.message || error.message || "Failed to disconnect account.",
        );
      }
    } finally {
      setAccountLoading(null);
    }
  };

  const connectAccount = async (strategy: "oauth_google" | "oauth_microsoft") => {
    if (!user) return;
    const account = await user.createExternalAccount({
      strategy,
      redirectUrl: window.location.href,
    });
    if (account.verification?.externalVerificationRedirectURL) {
      window.location.href = account.verification.externalVerificationRedirectURL.href;
    }
  };

  const verifiedConnect = useReverification(connectAccount);

  const handleConnect = async (strategy: "oauth_google" | "oauth_microsoft") => {
    if (!user) return;
    setAccountLoading(strategy);
    setDisconnectError(null);
    try {
      await verifiedConnect(strategy);
    } catch (error: any) {
      const isCancelled =
        error?.clerk_error === "reverification_cancelled" ||
        error?.code === "reverification_cancelled";
      if (!isCancelled) {
        console.error(error);
        setDisconnectError(
          error.errors?.[0]?.message || error.message || "Failed to connect account.",
        );
      }
      setAccountLoading(null);
    }
  };

  const googleAccount = user?.externalAccounts.find((a) => a.provider === "google");
  const microsoftAccount = user?.externalAccounts.find((a) => a.provider === "microsoft");

  return (
    <div className="mb-8 border-b border-gray-200 pb-8">
      <ProfileSectionTitle>Connected Accounts</ProfileSectionTitle>

      {disconnectError && (
        <Typography sx={{ fontSize: "13px", color: "var(--color-good-wine)", mb: 2 }}>
          {disconnectError}
        </Typography>
      )}

      {/* Google Account */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div
            className="rounded-lg bg-[#4285F4] flex items-center justify-center text-white font-bold text-[15px]"
            style={{ width: 34, height: 34 }}
          >
            G
          </div>
          <div>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "var(--color-rodeo-black)" }}
            >
              Google
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: "var(--color-graphite)" }}>
              {googleAccount?.emailAddress || "Not connected"}
            </Typography>
          </div>
        </div>
        {googleAccount ? (
          <div className="flex items-center gap-4">
            <span
              className="bg-smoke text-graphite uppercase text-[11px] font-bold rounded-full"
              style={{ padding: "4px 10px", letterSpacing: "0.04em" }}
            >
              Connected
            </span>
            <button
              type="button"
              onClick={() => handleDisconnect("oauth_google")}
              disabled={accountLoading === "oauth_google"}
              className="font-medium text-good-wine hover:text-good-wine-2 disabled:opacity-50"
              style={{ fontSize: "13px" }}
            >
              {accountLoading === "oauth_google" ? "..." : "Disconnect"}
            </button>
          </div>
        ) : (
          <Button
            variant="outlined"
            onClick={() => handleConnect("oauth_google")}
            disabled={accountLoading === "oauth_google"}
            sx={{
              color: "var(--color-rodeo-black)",
              borderColor: "var(--color-smoke)",
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "1.5px",
              padding: "8px 14px", // pf-btn-sm matches
              "&:hover": {
                borderColor: "var(--color-graphite)",
                backgroundColor: "var(--color-background)",
              },
            }}
          >
            {accountLoading === "oauth_google" ? "Connecting..." : "Connect"}
          </Button>
        )}
      </div>

      {/* Microsoft Account */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-4">
          <div
            className="rounded-lg bg-[#0078D4] flex items-center justify-center text-white font-bold text-[15px]"
            style={{ width: 34, height: 34 }}
          >
            M
          </div>
          <div>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "var(--color-rodeo-black)" }}
            >
              Microsoft
            </Typography>
            <Typography sx={{ fontSize: "12.5px", color: "var(--color-graphite)" }}>
              {microsoftAccount?.emailAddress || "Not connected"}
            </Typography>
          </div>
        </div>
        {microsoftAccount ? (
          <div className="flex items-center gap-4">
            <span
              className="bg-smoke text-graphite uppercase text-[11px] font-bold rounded-full"
              style={{ padding: "4px 10px", letterSpacing: "0.04em" }}
            >
              Connected
            </span>
            <button
              type="button"
              onClick={() => handleDisconnect("oauth_microsoft")}
              disabled={accountLoading === "oauth_microsoft"}
              className="font-medium text-good-wine hover:text-good-wine-2 disabled:opacity-50"
              style={{ fontSize: "13px" }}
            >
              {accountLoading === "oauth_microsoft" ? "..." : "Disconnect"}
            </button>
          </div>
        ) : (
          <Button
            variant="outlined"
            onClick={() => handleConnect("oauth_microsoft")}
            disabled={accountLoading === "oauth_microsoft"}
            sx={{
              color: "var(--color-rodeo-black)",
              borderColor: "var(--color-smoke)",
              textTransform: "uppercase",
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "1.5px",
              padding: "8px 14px",
              "&:hover": {
                borderColor: "var(--color-graphite)",
                backgroundColor: "var(--color-background)",
              },
            }}
          >
            {accountLoading === "oauth_microsoft" ? "Connecting..." : "Connect"}
          </Button>
        )}
      </div>
    </div>
  );
}
